import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FingerPrintIcon,
  DocumentTextIcon,
  ScaleIcon,
  ClockIcon,
  UsersIcon,
  NoSymbolIcon,
  PhoneArrowDownLeftIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarSquareIcon,
  BellAlertIcon,
  ClipboardDocumentCheckIcon,
  CodeBracketSquareIcon,
  ArrowDownTrayIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { PublicLayout } from '../../../components/layouts';
import { Seo } from '../../../seo/Seo';
import { HOME_FAQS } from '../../../seo/jsonLd';
import type { Locale } from '../../../seo/siteConfig';
import {
  DeckHero,
  DeckSection,
  DeckSectionHeader,
  DeckDisclaimer,
  DeckStepGrid,
  DeckPillarGrid,
  DeckFaqList,
  DeckFinalCta,
  useDeckAnimateOnScroll,
} from '../../../components/landing/deck';

const ComplyLandingPage: React.FC = () => {
  const { t, i18n } = useTranslation(['landing', 'common']);
  useDeckAnimateOnScroll();
  const locale: Locale = (i18n.language || 'es').startsWith('en') ? 'en' : 'es';

  const ootbDetections = [
    { key: 'sensitiveData', icon: FingerPrintIcon, tone: 'sky' as const },
    { key: 'mandatoryDisclosures', icon: DocumentTextIcon, tone: 'sky' as const },
  ];
  const configurableDetections = [
    { key: 'unfoundedThreats', icon: ScaleIcon, tone: 'amber' as const },
    { key: 'afterHours', icon: ClockIcon, tone: 'amber' as const },
    { key: 'thirdParty', icon: UsersIcon, tone: 'amber' as const },
    { key: 'scriptDeviations', icon: NoSymbolIcon, tone: 'amber' as const },
  ];

  const regulators = [
    { key: 'sedeco' },
    { key: 'condusef' },
    { key: 'ley2300' },
    { key: 'lgpd' },
    { key: 'sbs' },
    { key: 'cmf' },
  ] as const;

  const howItWorksSteps = [
    { key: 'ingestion', icon: PhoneArrowDownLeftIcon },
    { key: 'transcription', icon: DocumentMagnifyingGlassIcon },
    { key: 'evaluation', icon: ChartBarSquareIcon },
    { key: 'scoring', icon: BellAlertIcon },
  ] as const;

  const auditabilityPillars = [
    { key: 'snippet', icon: ClipboardDocumentCheckIcon },
    { key: 'criterion', icon: CodeBracketSquareIcon },
    { key: 'export', icon: ArrowDownTrayIcon },
  ] as const;

  const faqItems = (['regulators', 'evidence', 'data', 'false', 'speed'] as const).map((k) => ({
    question: t(`landing:compliancePage.faq.items.${k}.question`),
    answer: t(`landing:compliancePage.faq.items.${k}.answer`),
  }));

  return (
    <PublicLayout transparentHeader>
      <Seo route="/compliance" includeSoftwareApplication faqs={HOME_FAQS[locale]} />

      <DeckHero
        title={t('landing:compliancePage.hero.title')}
        subtitle={t('landing:compliancePage.hero.subtitle')}
        primaryCta={{ label: t('landing:compliancePage.hero.ctaPrimary'), to: '/contact' }}
        secondaryCta={{ label: t('landing:compliancePage.hero.ctaSecondary'), to: '#detections' }}
        announcement={t('landing:compliancePage.hero.announcement')}
      />

      <DeckDisclaimer
        text={t('landing:complianceDisclaimer.text')}
        linkLabel={t('landing:complianceDisclaimer.learnMore')}
        linkTo="/terms#section10"
      />

      <DeckSection id="detections">
        <DeckSectionHeader
          badge={t('landing:detectionsCatalog.sectionBadge')}
          title={t('landing:detectionsCatalog.sectionTitle')}
          subtitle={t('landing:detectionsCatalog.sectionSubtitle')}
        />
        <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-white/10">
          <div className="py-10 lg:pr-12 animate-on-scroll">
            <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-2">
              {t('landing:detectionsCatalog.outOfTheBox.title')}
            </p>
            <p className="text-sm text-white/60 mb-10">
              {t('landing:detectionsCatalog.outOfTheBox.subtitle')}
            </p>
            <div className="space-y-10">
              {ootbDetections.map(({ key, icon: Icon }) => (
                <div key={key} className="flex gap-4">
                  <Icon className="w-6 h-6 text-sky-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-base font-medium text-white mb-2">
                      {t(`landing:detectionsCatalog.outOfTheBox.items.${key}.title`)}
                    </h4>
                    <p className="text-sm text-white/50 leading-relaxed">
                      {t(`landing:detectionsCatalog.outOfTheBox.items.${key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="py-10 lg:pl-12 animate-on-scroll">
            <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">
              {t('landing:detectionsCatalog.configurable.title')}
            </p>
            <p className="text-sm text-white/60 mb-10">
              {t('landing:detectionsCatalog.configurable.subtitle')}
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-10">
              {configurableDetections.map(({ key, icon: Icon }) => (
                <div key={key} className="flex gap-4">
                  <Icon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-base font-medium text-white mb-2">
                      {t(`landing:detectionsCatalog.configurable.items.${key}.title`)}
                    </h4>
                    <p className="text-sm text-white/50 leading-relaxed">
                      {t(`landing:detectionsCatalog.configurable.items.${key}.description`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="mt-12 text-xs text-white/60 italic">
          {t('landing:detectionsCatalog.footnote')}
        </p>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:compliancePage.regulators.eyebrow')}
          title={t('landing:compliancePage.regulators.title')}
          subtitle={t('landing:compliancePage.regulators.subtitle')}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
          {regulators.map(({ key }) => (
            <div key={key} className="animate-on-scroll">
              <BuildingLibraryIcon className="w-6 h-6 text-sky-400 mb-4" />
              <p className="text-xs uppercase tracking-[0.15em] text-white/60 mb-1">
                {t(`landing:compliancePage.regulators.items.${key}.country`)}
              </p>
              <h3 className="text-2xl font-light text-white mb-4">
                {t(`landing:compliancePage.regulators.items.${key}.regulator`)}
              </h3>
              <p className="text-sm text-white/60 leading-relaxed">
                {t(`landing:compliancePage.regulators.items.${key}.covers`)}
              </p>
            </div>
          ))}
        </div>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:howItWorks.sectionBadge')}
          title={t('landing:howItWorks.sectionTitle')}
          subtitle={t('landing:howItWorks.sectionSubtitle')}
        />
        <DeckStepGrid
          steps={howItWorksSteps.map(({ key, icon }) => ({
            number: t(`landing:howItWorks.steps.${key}.number`),
            title: t(`landing:howItWorks.steps.${key}.title`),
            description: t(`landing:howItWorks.steps.${key}.description`),
            icon,
          }))}
        />
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:compliancePage.auditability.eyebrow')}
          title={t('landing:compliancePage.auditability.title')}
        />
        <DeckPillarGrid
          columns={3}
          pillars={auditabilityPillars.map(({ key, icon }) => ({
            icon,
            tone: 'sky',
            title: t(`landing:compliancePage.auditability.items.${key}.title`),
            description: t(`landing:compliancePage.auditability.items.${key}.description`),
          }))}
        />
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('landing:compliancePage.faq.eyebrow')}
          title={t('landing:compliancePage.faq.title')}
        />
        <DeckFaqList items={faqItems} />
      </DeckSection>

      <DeckFinalCta
        title={t('landing:compliancePage.finalCta.title')}
        subtitle={t('landing:compliancePage.finalCta.subtitle')}
        email="ventas@callcov.com"
        cta={{ label: t('landing:compliancePage.hero.ctaPrimary'), to: '/contact' }}
      />
    </PublicLayout>
  );
};

export default ComplyLandingPage;
